import { useState } from 'react';
import { uiSelectors } from 'src/selectors';
import { useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router';
// material
import { styled } from '@mui/material/styles';
// hooks
import useCollapseDrawer from 'src/hooks/useCollapseDrawer';
//
import { APP_BAR_DESKTOP, APP_BAR_MOBILE } from 'src/theme/layoutConstants';
import Navbar from './general/Navbar';
import Sidebar from './general/Sidebar';
import { DRAWER_WIDTH, COLLAPSE_WIDTH } from 'src/layouts/general/Navbar/index';
import { PATH_PROFILE, PATH_WORKSPACE } from 'src/routes/paths';
import ChatContextProvider from 'src/common/Chat/context/ChatContext';
import { currentWorkspaceSelectors } from 'src/selectors';
import { VoIPProvider, Dialer, VoIPDebugPanel } from 'src/common/VoIP';
// ----------------------------------------------------------------------

const RootStyle = styled('div')({
  overflow: 'hidden',
  height: '100%',
});

const MainStyle = styled('div', {
  shouldForwardProp: (prop) =>
    !['showDrawer', 'isCollapse', 'isLoggedAsCustomer'].includes(prop),
})(({ theme, showDrawer, isCollapse, isLoggedAsCustomer }) => {
  const isLight = theme.palette.mode === 'light';
  const scrollbarThumbColor = isLight ? '#bebfc1ff' : '#4A5568';

  return {
    height: `calc(100% - ${APP_BAR_MOBILE}px)`,
    paddingTop: '4px',
    marginTop: APP_BAR_MOBILE,
    [theme.breakpoints.up('lg')]: {
      height: `calc(100% - ${APP_BAR_DESKTOP}px)`,
      marginTop: APP_BAR_DESKTOP,
      ...(showDrawer &&
        !isLoggedAsCustomer && {
          marginLeft: DRAWER_WIDTH,
        }),
      ...(isCollapse &&
        !isLoggedAsCustomer && {
          marginLeft: COLLAPSE_WIDTH,
        }),
    },

    // 1. Firefox: Set track to 'transparent' (format: 'thumb track')
    scrollbarColor: `${scrollbarThumbColor} transparent`,
    scrollbarWidth: 'thin',

    // 2. WebKit (Chrome, Edge, Safari) for Parent
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent', // Makes track transparent
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: scrollbarThumbColor,
      borderRadius: '4px',
    },

    // 3. Deep Targeting for Children
    '& *': {
      scrollbarColor: `${scrollbarThumbColor} transparent`, // Firefox children
      scrollbarWidth: 'thin',

      '&::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: 'transparent', // WebKit children
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: scrollbarThumbColor,
        borderRadius: '4px',
      },
    },
  };
});

// ----------------------------------------------------------------------

export default function DashboardLayout({ children }) {
  const workspace = useSelector(currentWorkspaceSelectors.getCurrentWorkspace);
  const { pathname } = useLocation();
  const { isCollapse } = useCollapseDrawer();
  const [open, setOpen] = useState(false);
  const isLoggedAsCustomer = useSelector(uiSelectors.isLoggedAsCustomer);
  const PAGES_WITH_BASIC_LAYOUT = ['monitoring', 'traffic'];

  // TODO: mejorar esta logica de layout

  const showBasicLayout =
    !!workspace?.id &&
    PAGES_WITH_BASIC_LAYOUT.some((page) => pathname.includes(page));

  const showDrawer =
    pathname.includes(PATH_WORKSPACE.root.split(':')[0]) ||
    pathname === PATH_PROFILE.root;

  if (showBasicLayout) {
    return (
      <VoIPProvider>
        <ChatContextProvider>
          <RootStyle>
            <Navbar noSideBar />
            <MainStyle>
              <Outlet />
              {children}
            </MainStyle>
            <Dialer />
            <VoIPDebugPanel />
          </RootStyle>
        </ChatContextProvider>
      </VoIPProvider>
    );
  }

  return (
    <VoIPProvider>
      <ChatContextProvider>
        <RootStyle>
          <Navbar onOpenSidebar={() => setOpen(true)} />
          {!isLoggedAsCustomer && (
            <Sidebar
              isOpenSidebar={open}
              onCloseSidebar={() => setOpen(false)}
            />
          )}
          <MainStyle
            isCollapse={isCollapse}
            showDrawer={showDrawer}
            isLoggedAsCustomer={isLoggedAsCustomer}
          >
            <Outlet />
            {children}
          </MainStyle>
          <Dialer />
          <VoIPDebugPanel />
        </RootStyle>
      </ChatContextProvider>
    </VoIPProvider>
  );
}
